import { Button, Modal, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { SalaryAdvanceStatus } from "@/modules/hr/salary-advance/domain/contracts/types";
import { formatDateLocal } from "@/shared/lib/date-time";
import {
  useCancelSalaryAdvanceMutation,
  useMyAdvanceDetailQuery,
} from "../api/queries";
import { AdvanceStatusBadge } from "../components/AdvanceStatusBadge";
import { RepaymentScheduleTable } from "../components/RepaymentScheduleTable";

function formatLAK(amount: number) {
  return `${new Intl.NumberFormat("lo-LA").format(amount)} ກີບ`;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return formatDateLocal(iso);
}

const LAO_MONTHS = [
  "",
  "ມັງກອນ",
  "ກຸມພາ",
  "ມີນາ",
  "ເມສາ",
  "ພຶດສະພາ",
  "ມິຖຸນາ",
  "ກໍລະກົດ",
  "ສິງຫາ",
  "ກັນຍາ",
  "ຕຸລາ",
  "ພະຈິກ",
  "ທັນວາ",
];

const STATUS_BANNER: Record<
  SalaryAdvanceStatus,
  { label: string; desc: string; color: string }
> = {
  pending: {
    label: "ລໍຖ້າການອະນຸມັດ",
    desc: "ຄຳຂໍຂອງທ່ານກຳລັງລໍຖ້າ HR ກວດສອບ",
    color: "border-yellow-200 bg-yellow-50 text-yellow-800",
  },
  approved: {
    label: "ໄດ້ຮັບການອະນຸມັດ",
    desc: "ຄຳຂໍໄດ້ຮັບການອະນຸມັດ ລໍຖ້າການໂອນເງິນ",
    color: "border-blue-200 bg-blue-50 text-blue-800",
  },
  rejected: {
    label: "ຄຳຂໍຖືກປະຕິເສດ",
    desc: "",
    color: "border-red-200 bg-red-50 text-red-800",
  },
  paid: {
    label: "ໂອນເງິນແລ້ວ",
    desc: "ໄດ້ຮັບເງິນແລ້ວ ກຳລັງຫັກຄືນຕາມຕາຕະລາງ",
    color: "border-purple-200 bg-purple-50 text-purple-800",
  },
  cancelled: {
    label: "ຄຳຂໍຖືກຍົກເລີກ",
    desc: "ຄຳຂໍນີ້ຖືກຍົກເລີກແລ້ວ",
    color: "border-gray-200 bg-gray-50 text-gray-600",
  },
  fully_repaid: {
    label: "ຫັກຄືນຄົບແລ້ວ",
    desc: "ທ່ານຊຳລະຄຳຂໍນີ້ຄົບຖ້ວນແລ້ວ",
    color: "border-green-200 bg-green-50 text-green-800",
  },
};

export function MyAdvanceDetailPage() {
  const nav = useNavigate();
  const { id } = useParams({ strict: false }) as { id: string };

  const detailQuery = useMyAdvanceDetailQuery(id);
  const advance = detailQuery.data;

  const cancelMutation = useCancelSalaryAdvanceMutation();
  const [cancelOpen, setCancelOpen] = useState(false);

  const handleCancelConfirm = useCallback(async () => {
    if (!id) return;
    try {
      await cancelMutation.mutateAsync(id);
      toast.success("ຍົກເລີກຄຳຂໍສຳເລັດ");
      setCancelOpen(false);
      detailQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    }
  }, [id, cancelMutation, detailQuery]);

  if (detailQuery.isLoading || !advance) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </Main>
      </>
    );
  }

  const banner = STATUS_BANNER[advance.status];

  return (
    <>
      <Header />
      <Main>
        {/* Toolbar */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav({ to: "/app/hr/salary-advance/requests" })}
              className="gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-2xl">ລາຍລະອຽດຄຳຂໍ</h1>
                <AdvanceStatusBadge status={advance.status} />
              </div>
              <p className="mt-0.5 text-muted-foreground text-sm">
                ຍື່ນວັນທີ {formatDateLocal(advance.requestDate)}
              </p>
            </div>
          </div>

          {advance.status === "pending" && (
            <Button variant="destructive" onClick={() => setCancelOpen(true)}>
              ຍົກເລີກຄຳຂໍ
            </Button>
          )}
        </div>

        <div className="mx-auto grid w-full max-w-2xl gap-5">
          {/* Status banner */}
          <div className={`rounded-xl border p-4 ${banner.color}`}>
            <p className="font-semibold">{banner.label}</p>
            {advance.status === "rejected" ? (
              <p className="mt-1 text-sm">
                {advance.rejectionReason ?? "ບໍ່ລະບຸເຫດຜົນ"}
              </p>
            ) : (
              banner.desc && <p className="mt-1 text-sm">{banner.desc}</p>
            )}
          </div>

          {/* คำขอ */}
          <section className="rounded-xl border bg-card p-6">
            <h2 className="mb-4 font-semibold">ຂໍ້ມູນຄຳຂໍ</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">ວັນທີ່ຍື່ນ</span>
                <p className="mt-0.5 font-medium">
                  {formatDateLocal(advance.requestDate)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">ຈຳນວນທີ່ຂໍ</span>
                <p className="mt-0.5 font-medium">
                  {formatLAK(advance.amount)}
                </p>
              </div>
              {advance.reason && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">ເຫດຜົນ</span>
                  <p className="mt-0.5">{advance.reason}</p>
                </div>
              )}
            </div>
          </section>

          {/* อนุมัติ */}
          {(advance.status === "approved" ||
            advance.status === "paid" ||
            advance.status === "fully_repaid") && (
            <section className="rounded-xl border bg-card p-6">
              <h2 className="mb-4 font-semibold">ລາຍລະອຽດການອະນຸມັດ</h2>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div>
                  <span className="text-muted-foreground">ຈຳນວນທີ່ໄດ້ຮັບ</span>
                  <p className="mt-0.5 font-semibold text-base">
                    {advance.approvedAmount != null
                      ? formatLAK(advance.approvedAmount)
                      : "—"}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">ແຜນຫັກຄືນ</span>
                  <p className="mt-0.5 font-medium">
                    {advance.repaymentMonths} ງວດ
                  </p>
                  {advance.repaymentStartYear &&
                    advance.repaymentStartMonth && (
                      <p className="text-muted-foreground text-xs">
                        ເລີ່ມ {LAO_MONTHS[advance.repaymentStartMonth]}{" "}
                        {advance.repaymentStartYear}
                      </p>
                    )}
                </div>
                {advance.notes && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">ໝາຍເຫດຈາກ HR</span>
                    <p className="mt-0.5">{advance.notes}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* จ่ายเงิน */}
          {(advance.status === "paid" || advance.status === "fully_repaid") && (
            <section className="rounded-xl border bg-card p-6">
              <h2 className="mb-2 font-semibold">ການໂອນເງິນ</h2>
              <p className="text-muted-foreground text-sm">
                ໂອນເງິນໃນວັນທີ {formatDate(advance.paidAt)}
              </p>
            </section>
          )}

          {/* ตารางหักคืน */}
          {advance.repayments.length > 0 && (
            <section className="rounded-xl border bg-card p-6">
              <h2 className="mb-4 font-semibold">ຕາຕະລາງຫັກຄືນ</h2>
              <RepaymentScheduleTable repayments={advance.repayments} />
            </section>
          )}
        </div>

        {/* Cancel Dialog */}
        <Modal
          open={cancelOpen}
          onOpenChange={(open) => !open && setCancelOpen(false)}
          title="ຢືນຢັນຍົກເລີກຄຳຂໍ"
          description="ທ່ານຕ້ອງການຍົກເລີກຄຳຂໍເບິກເງິນເດືອນລ່ວງໜ້ານີ້ບໍ? ເມື່ອຍົກເລີກແລ້ວ ຈະບໍ່ສາມາດກູ້ຄືນໄດ້."
          footer={
            <>
              <Button variant="outline" onClick={() => setCancelOpen(false)}>
                ບໍ່ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelConfirm}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? "ກຳລັງດຳເນີນການ..." : "ຢືນຢັນຍົກເລີກ"}
              </Button>
            </>
          }
        />
      </Main>
    </>
  );
}
